/**
 * FindNewPartnerDialog — re-pair a team-roping entry whose previous
 * partner declined.
 *
 * The dashboard surfaces a red "no partner" warning on entries in the
 * `needs_partner` state; clicking the CTA opens this dialog. We reuse
 * the same PartnerSearchCombobox as the registration wizard so the
 * search UX is identical, but the submit path hits the new
 * /registration-ops/partner/:id/reinvite endpoint instead of cancelling
 * + re-registering. That preserves the original transaction-fee
 * payment so the user isn't penalised for their first partner backing
 * out.
 *
 * When the originating user paid for the original team
 * (paid_for_partner=true), picking a new partner can change the team
 * total — the new partner's membership status may differ from the
 * rejected one's. Backend computes the cost reconciliation server-side
 * (charge or refund of the difference, plus a flat partner-swap
 * adjustment fee) and surfaces it via /partner-fee-preview. This
 * dialog renders that disclosure inline and forces an explicit "I
 * approve" tick before the invitation goes out, so we never move money
 * on the user's behalf without consent.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowDown, ArrowUp, Check } from 'lucide-react';
import { PartnerSearchCombobox } from './PartnerSearchCombobox';
import {
  usePartnerFeePreview,
  type PartnerSearchResult,
} from '@/api/extensions/usePartnerSearch';
import { useReinvitePartner } from '@/api/extensions/useRegistrationOps';
import { useToast } from '@/contexts/ToastContext';
import { formatCurrency } from '@/utils/formatters';

interface FindNewPartnerDialogProps {
  open: boolean;
  onClose: () => void;
  registrationId: string;
  /** The event UUID the entry belongs to. Required for the partner-fee-preview lookup. */
  eventId?: string | null;
  /** Primary's role on the entry — used to label the partner role we're searching for. */
  primaryPosition?: string | null;
  /** Optional event display label for the modal title. */
  eventName?: string;
}

export function FindNewPartnerDialog({
  open,
  onClose,
  registrationId,
  eventId,
  primaryPosition,
  eventName,
}: FindNewPartnerDialogProps) {
  const { showSuccess, showError } = useToast();
  const reinvite = useReinvitePartner();

  const [selectedPartner, setSelectedPartner] =
    useState<PartnerSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approved, setApproved] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedPartner(null);
      setError(null);
      setApproved(false);
    }
  }, [open]);

  // Reset the approval tick whenever the picked partner changes — the
  // disclosure shows that partner's specific cost, so a stale tick
  // from a previous selection must NOT carry over.
  useEffect(() => {
    setApproved(false);
  }, [selectedPartner?.id]);

  const partnerPosition =
    (primaryPosition ?? '').toLowerCase() === 'header' ? 'Heeler' : 'Header';

  const { data: preview, isLoading: previewLoading } = usePartnerFeePreview(
    eventId ?? null,
    selectedPartner?.id ?? null,
    registrationId,
  );

  const reconciliation = preview?.cost_reconciliation ?? null;
  // When there's no money movement (no bulk capture, or the picked
  // partner's seat costs exactly the same as the original), the
  // approval gate is unnecessary — sending the invite has no
  // financial side-effect, so we let the user hit Send straight away.
  const requiresApproval =
    !!reconciliation &&
    (reconciliation.direction === 'charge' ||
      reconciliation.direction === 'refund');
  const canSubmit =
    !!selectedPartner?.id &&
    !reinvite.isPending &&
    (!requiresApproval || approved);

  const handleSubmit = async () => {
    if (!selectedPartner?.id) {
      setError('Please pick a partner from the search results.');
      return;
    }
    if (requiresApproval && !approved) {
      setError(
        'Please confirm you understand what will happen on acceptance.',
      );
      return;
    }
    setError(null);
    try {
      await reinvite.mutateAsync({
        registrationId,
        partner_user_profile_id: selectedPartner.id,
      });
      showSuccess(
        'Invitation sent',
        'Your new partner will get a notification to accept or decline.',
      );
      onClose();
    } catch (err: any) {
      const message =
        err?.response?.data?.error ??
        err?.message ??
        'Could not send the invitation. Please try again.';
      setError(message);
      showError('Could not send invitation', message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Find a new partner</DialogTitle>
          <DialogDescription>
            {eventName
              ? `Send a fresh invitation for your ${eventName} entry.`
              : 'Send a fresh invitation for this entry.'}{' '}
            Your registration and transaction fee stay in place — only
            the partner pairing changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="text-xs text-muted-foreground">
            You'll be the {primaryPosition || 'primary'}; we're searching
            for a {partnerPosition.toLowerCase()}.
          </div>
          <PartnerSearchCombobox
            value={selectedPartner}
            onChange={(user) => {
              setSelectedPartner(user);
              setError(null);
            }}
            partnerPosition={partnerPosition}
            error={error ?? undefined}
            eventId={eventId ?? null}
          />

          {selectedPartner?.id && previewLoading && (
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              Calculating cost difference…
            </div>
          )}

          {selectedPartner?.id &&
            !previewLoading &&
            reconciliation &&
            reconciliation.direction === 'charge' && (
              <div className="rounded-md border border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 p-3 text-xs space-y-2">
                <div className="flex items-start gap-2 text-amber-900 dark:text-amber-200">
                  <ArrowUp className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <div className="font-semibold">
                      Additional charge if they accept
                    </div>
                    <div className="leading-snug text-amber-800/90 dark:text-amber-200/90">
                      {reconciliation.explanation}
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1 text-[11px] text-amber-800/90 dark:text-amber-200/90">
                      <span>Cost difference</span>
                      <span className="text-right font-medium">
                        {formatCurrency(reconciliation.diff)}
                      </span>
                      <span>Processing fee</span>
                      <span className="text-right font-medium">
                        {formatCurrency(reconciliation.adjustment_fee)}
                      </span>
                      <span className="font-semibold">You'll be charged</span>
                      <span className="text-right font-semibold">
                        {formatCurrency(reconciliation.net_amount)}
                      </span>
                    </div>
                  </div>
                </div>
                <label className="flex items-start gap-2 cursor-pointer text-amber-900 dark:text-amber-200">
                  <input
                    type="checkbox"
                    checked={approved}
                    onChange={(e) => setApproved(e.target.checked)}
                    className="mt-0.5 rounded border-amber-400"
                  />
                  <span className="text-[11px] leading-snug">
                    I approve being charged{' '}
                    <span className="font-semibold">
                      {formatCurrency(reconciliation.net_amount)}
                    </span>{' '}
                    if {preview?.partner_display_name ?? 'this partner'}{' '}
                    accepts the invitation.
                  </span>
                </label>
              </div>
            )}

          {selectedPartner?.id &&
            !previewLoading &&
            reconciliation &&
            reconciliation.direction === 'refund' && (
              <div className="rounded-md border border-emerald-300 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 p-3 text-xs space-y-2">
                <div className="flex items-start gap-2 text-emerald-900 dark:text-emerald-200">
                  <ArrowDown className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <div className="font-semibold">
                      Refund if they accept
                    </div>
                    <div className="leading-snug text-emerald-800/90 dark:text-emerald-200/90">
                      {reconciliation.explanation}
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1 text-[11px] text-emerald-800/90 dark:text-emerald-200/90">
                      <span>Surplus</span>
                      <span className="text-right font-medium">
                        {formatCurrency(Math.abs(reconciliation.diff))}
                      </span>
                      <span>Processing fee</span>
                      <span className="text-right font-medium">
                        −{formatCurrency(reconciliation.adjustment_fee)}
                      </span>
                      <span className="font-semibold">Net refund</span>
                      <span className="text-right font-semibold">
                        {formatCurrency(reconciliation.net_amount)}
                      </span>
                    </div>
                  </div>
                </div>
                <label className="flex items-start gap-2 cursor-pointer text-emerald-900 dark:text-emerald-200">
                  <input
                    type="checkbox"
                    checked={approved}
                    onChange={(e) => setApproved(e.target.checked)}
                    className="mt-0.5 rounded border-emerald-400"
                  />
                  <span className="text-[11px] leading-snug">
                    I approve a refund of{' '}
                    <span className="font-semibold">
                      {formatCurrency(reconciliation.net_amount)}
                    </span>{' '}
                    {reconciliation.net_amount > 0
                      ? 'to my original payment method '
                      : ''}
                    if {preview?.partner_display_name ?? 'this partner'}{' '}
                    accepts.
                  </span>
                </label>
              </div>
            )}

          {selectedPartner?.id &&
            !previewLoading &&
            reconciliation &&
            reconciliation.direction === 'noop' && (
              <div className="flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{reconciliation.explanation}</span>
              </div>
            )}

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={reinvite.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {reinvite.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
